import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  UserPlus, 
  Edit2, 
  Trash2, 
  Lock, 
  AlertCircle, 
  CheckCircle,
  Search,
  Filter,
  UserCheck,
  UserX,
  Shield,
  ShieldOff,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import './ManageUsers.css';
import api, { getGroups, getUserGroups, setUserGroups } from '../services/api';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filterActive, setFilterActive] = useState('all'); // all, active, inactive
  const [filterGroup, setFilterGroup] = useState('all'); // all | group id
  const [filterRole, setFilterRole] = useState('all'); // all, admin, user
  const [sortBy, setSortBy] = useState('name'); // name, username, status, created_at, role
  const [sortDir, setSortDir] = useState('asc'); // asc, desc
  const searchDebounceReady = useRef(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [userGroupIds, setUserGroupIds] = useState([]);
  const [userAdminGroupIds, setUserAdminGroupIds] = useState([]);

  const { user, isAdmin, canManageUsers, adminGroupIds, isGroupAdmin } = useAuth();
  const navigate = useNavigate();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      if (debouncedSearch.trim()) {
        params.set('search', debouncedSearch.trim());
      }
      if (filterActive === 'active') {
        params.set('is_active', 'true');
      } else if (filterActive === 'inactive') {
        params.set('is_active', 'false');
      }
      if (filterRole === 'admin') {
        params.set('is_admin', 'true');
      } else if (filterRole === 'user') {
        params.set('is_admin', 'false');
      }
      if (filterGroup !== 'all') {
        params.set('group_id', String(filterGroup));
      }
      params.set('sort_by', sortBy);
      params.set('sort_dir', sortDir);

      const response = await api.get(`/users?${params.toString()}`);
      const raw = response.data;
      let baseUsers = [];
      let totalCount = 0;
      let pagesCount = 1;

      // API nova: { items, total, total_pages } | API antiga (produção): array direto
      if (Array.isArray(raw)) {
        let list = [...raw];
        const q = debouncedSearch.trim().toLowerCase();
        if (q) {
          list = list.filter(
            (u) =>
              (u.username || '').toLowerCase().includes(q) ||
              (u.email || '').toLowerCase().includes(q) ||
              (u.full_name || '').toLowerCase().includes(q)
          );
        }
        if (filterActive === 'active') {
          list = list.filter((u) => u.is_active);
        } else if (filterActive === 'inactive') {
          list = list.filter((u) => !u.is_active);
        }
        if (filterRole === 'admin') {
          list = list.filter((u) => u.is_admin);
        } else if (filterRole === 'user') {
          list = list.filter((u) => !u.is_admin);
        }

        // Carregar grupos antes de filtrar por setor (API antiga)
        const groupsPerUserLegacy = await Promise.all(
          list.map(async (u) => {
            try {
              const gr = await getUserGroups(u.id);
              return (gr.data?.groups || []).map((g) => ({ id: g.id, name: g.name }));
            } catch {
              return [];
            }
          })
        );
        list = list.map((u, idx) => ({ ...u, groups: groupsPerUserLegacy[idx] }));

        if (filterGroup !== 'all') {
          const gid = Number(filterGroup);
          list = list.filter((u) => (u.groups || []).some((g) => g.id === gid));
        }

        const dir = sortDir === 'asc' ? 1 : -1;
        list.sort((a, b) => {
          const pick = (u) => {
            if (sortBy === 'name') return (u.full_name || u.username || '').toLowerCase();
            if (sortBy === 'username') return (u.username || '').toLowerCase();
            if (sortBy === 'status') return u.is_active ? 1 : 0;
            if (sortBy === 'role') return u.is_admin ? 1 : 0;
            return new Date(u.created_at || 0).getTime();
          };
          const va = pick(a);
          const vb = pick(b);
          if (va < vb) return -1 * dir;
          if (va > vb) return 1 * dir;
          return 0;
        });

        totalCount = list.length;
        pagesCount = Math.max(1, Math.ceil(totalCount / pageSize) || 1);
        const effectivePage = Math.min(Math.max(1, page), pagesCount);
        const start = (effectivePage - 1) * pageSize;
        baseUsers = list.slice(start, start + pageSize);
        if (effectivePage !== page) {
          setPage(effectivePage);
        }
        setTotal(totalCount);
        setTotalPages(pagesCount);
        setUsers(baseUsers);
        return;
      } else {
        const data = raw || {};
        baseUsers = data.items || [];
        totalCount = Number(data.total ?? 0);
        pagesCount = Math.max(1, Number(data.total_pages ?? 1) || 1);
      }

      setTotal(totalCount);
      setTotalPages(pagesCount);

      // API já envia groups em lote — evita N requests /users/{id}/groups
      const alreadyHasGroups = baseUsers.every((u) => Array.isArray(u.groups));
      if (alreadyHasGroups) {
        setUsers(baseUsers);
      } else {
        try {
          const groupsPerUser = await Promise.all(
            baseUsers.map(async (u) => {
              try {
                const gr = await getUserGroups(u.id);
                return (gr.data?.groups || []).map((g) => ({ id: g.id, name: g.name }));
              } catch {
                return [];
              }
            })
          );
          const withGroups = baseUsers.map((u, idx) => ({ ...u, groups: groupsPerUser[idx] }));
          setUsers(withGroups);
        } catch {
          setUsers(baseUsers);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, filterActive, filterGroup, filterRole, sortBy, sortDir]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      if (searchDebounceReady.current) {
        setPage(1);
      } else {
        searchDebounceReady.current = true;
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!canManageUsers) {
      navigate('/atendimento');
      return;
    }
    fetchUsers();
  }, [canManageUsers, navigate, fetchUsers]);

  useEffect(() => {
    if (!canManageUsers) return;
    getGroups()
      .then((res) => {
        const groups = res.data || [];
        if (isAdmin) {
          setAllGroups(groups);
        } else {
          const allowed = new Set((adminGroupIds || []).map(Number));
          setAllGroups(groups.filter((g) => allowed.has(Number(g.id))));
        }
      })
      .catch(() => setAllGroups([]));
  }, [canManageUsers, isAdmin, adminGroupIds]);

  const applyToolbarFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setFilterActive('all');
    setFilterGroup('all');
    setFilterRole('all');
    setSortBy('name');
    setSortDir('asc');
    setPage(1);
  };

  const toggleUserActive = async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/toggle-active`);
      setSuccess(response.data.message);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao alterar status do usuário:', err);
      setError(err.response?.data?.detail || 'Erro ao alterar status do usuário');
    }
  };

  const deleteUser = async () => {
    if (!selectedUser) return;

    try {
      await api.delete(`/users/${selectedUser.id}`);
      setSuccess('Usuário deletado com sucesso');
      fetchUsers();
      setShowDeleteModal(false);
      setSelectedUser(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao deletar usuário:', err);
      setError(err.response?.data?.detail || 'Erro ao deletar usuário');
    }
  };

  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container">
      <div className="manageusers-header">
        <h1 className="manageusers-page-title">Gerenciar Usuários</h1>
        <p className="manageusers-page-subtitle">Administre usuários do sistema</p>
      </div>

      {error && (
        <div className="manageusers-error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {success && (
        <div className="manageusers-success-message">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      <div className="manageusers-card">
        <div className="manageusers-users-header">
          <div className="manageusers-search-bar">
            <Search size={18} className="manageusers-search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome, email ou usuário..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="manageusers-search-input"
            />
          </div>

          <button
            className="manageusers-filter-btn manageusers-filter-btn-primary"
            onClick={() => navigate('/register')}
          >
            <UserPlus size={16} />
            Novo Usuário
          </button>
        </div>

        <div className="manageusers-filters-toolbar">
          <div className="manageusers-filter-field">
            <label htmlFor="filter-status">
              <Filter size={14} />
              Status
            </label>
            <select
              id="filter-status"
              className="manageusers-select"
              value={filterActive}
              onChange={(e) => applyToolbarFilter(setFilterActive)(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>
          </div>

          <div className="manageusers-filter-field">
            <label htmlFor="filter-group">
              <Users size={14} />
              Setor / Grupo
            </label>
            <select
              id="filter-group"
              className="manageusers-select"
              value={filterGroup}
              onChange={(e) => applyToolbarFilter(setFilterGroup)(e.target.value)}
            >
              <option value="all">Todos os setores</option>
              {allGroups.map((g) => (
                <option key={g.id} value={String(g.id)}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>

          <div className="manageusers-filter-field">
            <label htmlFor="filter-role">
              <Shield size={14} />
              Tipo
            </label>
            <select
              id="filter-role"
              className="manageusers-select"
              value={filterRole}
              onChange={(e) => applyToolbarFilter(setFilterRole)(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="admin">Administradores</option>
              <option value="user">Usuários</option>
            </select>
          </div>

          <div className="manageusers-filter-field">
            <label htmlFor="sort-by">
              Organizar por
            </label>
            <select
              id="sort-by"
              className="manageusers-select"
              value={sortBy}
              onChange={(e) => applyToolbarFilter(setSortBy)(e.target.value)}
            >
              <option value="name">Nome</option>
              <option value="username">Usuário</option>
              <option value="status">Status</option>
              <option value="role">Tipo</option>
              <option value="created_at">Data de criação</option>
            </select>
          </div>

          <div className="manageusers-filter-field">
            <label htmlFor="sort-dir">
              Ordem
            </label>
            <select
              id="sort-dir"
              className="manageusers-select"
              value={sortDir}
              onChange={(e) => applyToolbarFilter(setSortDir)(e.target.value)}
            >
              <option value="asc">Crescente (A→Z)</option>
              <option value="desc">Decrescente (Z→A)</option>
            </select>
          </div>

          <div className="manageusers-filter-field manageusers-filter-field-action">
            <label>&nbsp;</label>
            <button type="button" className="manageusers-filter-btn" onClick={clearFilters}>
              Limpar filtros
            </button>
          </div>
        </div>

        {loading ? (
          <div className="manageusers-loading-container">
            <div className="manageusers-loading-spinner-large"></div>
            <p>Carregando usuários...</p>
          </div>
        ) : (
          <div className="manageusers-users-table-container">
            <table className="manageusers-users-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Email</th>
                  <th>Nome Completo</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Setor</th>
                  <th>Último acesso</th>
                  <th>Data de Criação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="manageusers-no-users">
                      <Users size={48} />
                      <p>Nenhum usuário encontrado</p>
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className={!u.is_active ? 'manageusers-user-inactive' : ''}>
                      <td>
                        <div className="manageusers-user-cell">
                          <div className={`manageusers-user-avatar ${u.is_admin ? 'manageusers-user-avatar-admin' : ''}`}>
                            {u.username.charAt(0).toUpperCase()}
                          </div>
                          <span className="manageusers-username">{u.username}</span>
                        </div>
                      </td>
                      <td>{u.email}</td>
                      <td>{u.full_name || '-'}</td>
                      <td>
                        <span className={`manageusers-badge ${u.is_admin ? 'manageusers-badge-admin' : 'manageusers-badge-user'}`}>
                          {u.is_admin ? (
                            <>
                              <Shield size={14} />
                              Admin
                            </>
                          ) : (
                            <>
                              <ShieldOff size={14} />
                              Usuário
                            </>
                          )}
                        </span>
                      </td>
                      <td>
                        <span className={`manageusers-badge ${u.is_active ? 'manageusers-badge-active' : 'manageusers-badge-inactive'}`}>
                          {u.is_active ? (
                            <>
                              <UserCheck size={14} />
                              Ativo
                            </>
                          ) : (
                            <>
                              <UserX size={14} />
                              Inativo
                            </>
                          )}
                        </span>
                      </td>
                      <td>
                        {(u.groups && u.groups.length > 0) ? (
                          <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                            {u.groups.map(g => (
                              <span key={g.id} className="manageusers-badge">{g.name}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="manageusers-muted">—</span>
                        )}
                      </td>
                      <td>
                        {u.last_seen ? (
                          <span title={formatDate(u.last_seen)}>{formatDate(u.last_seen)}</span>
                        ) : (
                          <span className="manageusers-muted" title="Nunca acessou ou sem registro">Nunca</span>
                        )}
                      </td>
                      <td>{formatDate(u.created_at)}</td>
                      <td>
                        <div className="manageusers-action-buttons">
                          <button
                            className="manageusers-action-btn edit"
                            onClick={() => {
                              setSelectedUser(u);
                              setShowEditModal(true);
                            }}
                            title="Editar usuário"
                          >
                            <Edit2 size={16} />
                          </button>
                          
                          <button
                            className="manageusers-action-btn reset"
                            onClick={() => {
                              setSelectedUser(u);
                              setShowResetPasswordModal(true);
                            }}
                            title="Resetar senha"
                          >
                            <Lock size={16} />
                          </button>

                          <button
                            className="manageusers-action-btn edit"
                            onClick={async () => {
                              setSelectedUser(u);
                              try {
                                const ugrs = await getUserGroups(u.id);
                                const groups = ugrs.data?.groups || [];
                                setUserGroupIds(groups.map((g) => g.id));
                                setUserAdminGroupIds(
                                  groups.filter((g) => g.is_group_admin).map((g) => g.id)
                                );
                                setShowGroupsModal(true);
                              } catch (e) {
                                setError('Erro ao carregar grupos');
                              }
                            }}
                            title="Editar setores/grupos"
                          >
                            Setores
                          </button>

                          <button
                            className={`manageusers-action-btn manageusers-action-btn-toggle ${u.is_active ? '' : 'manageusers-action-btn-activate'}`}
                            onClick={() => toggleUserActive(u.id)}
                            title={u.is_active ? 'Desativar usuário' : 'Ativar usuário'}
                            disabled={u.id === user.id}
                          >
                            {u.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                          </button>

                          <button
                            className="manageusers-action-btn delete"
                            onClick={() => {
                              setSelectedUser(u);
                              setShowDeleteModal(true);
                            }}
                            title="Deletar usuário"
                            disabled={u.id === user.id}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {!loading && (
              <div className="manageusers-pagination">
                <span className="manageusers-pagination-info">
                  Mostrando {rangeFrom}–{rangeTo} de {total}
                </span>
                <div className="manageusers-pagination-actions">
                  <button
                    type="button"
                    className="manageusers-pagination-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    aria-label="Página anterior"
                  >
                    <ChevronLeft size={18} />
                    Anterior
                  </button>
                  <span className="manageusers-pagination-page">
                    Página {page} de {totalPages || 1}
                  </span>
                  <button
                    type="button"
                    className="manageusers-pagination-btn"
                    onClick={() => setPage((p) => Math.min(totalPages || 1, p + 1))}
                    disabled={page >= (totalPages || 1) || total === 0}
                    aria-label="Próxima página"
                  >
                    Próxima
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Edição */}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          allowAdminToggle={isAdmin}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            fetchUsers();
            setSuccess('Usuário atualizado com sucesso');
            setTimeout(() => setSuccess(''), 3000);
          }}
          onError={setError}
        />
      )}

      {/* Modal de Grupos / Setores */}
      {showGroupsModal && (
        <div className="manageusers-modal-overlay" onClick={() => setShowGroupsModal(false)}>
          <div className="manageusers-modal" onClick={(e)=>e.stopPropagation()}>
            <h3>Setores de {selectedUser?.full_name || selectedUser?.username}</h3>
            <p className="manageusers-muted" style={{marginTop: 4, marginBottom: 8}}>
              {isAdmin
                ? 'Marque o setor e, se desejar, defina o usuário como diretor daquele setor.'
                : 'Você só pode vincular pessoas aos setores sob sua direção.'}
            </p>
            <div className="manageusers-form-group" style={{maxHeight: 320, overflowY: 'auto', marginTop: 8}}>
              {(allGroups || []).map(g => (
                <div key={g.id} style={{display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderBottom:'1px solid var(--border-color)'}}>
                  <label style={{display:'flex', alignItems:'center', gap:8, flex:1}}>
                    <input
                      type="checkbox"
                      checked={userGroupIds.includes(g.id)}
                      onChange={(e)=>{
                        if (e.target.checked) setUserGroupIds(prev => [...prev, g.id]);
                        else {
                          setUserGroupIds(prev => prev.filter(id => id !== g.id));
                          setUserAdminGroupIds(prev => prev.filter(id => id !== g.id));
                        }
                      }}
                    />
                    <span>{g.name}</span>
                  </label>
                  {isAdmin && (
                    <label style={{display:'flex', alignItems:'center', gap:6, fontSize:13, opacity: userGroupIds.includes(g.id) ? 1 : 0.4}}>
                      <input
                        type="checkbox"
                        disabled={!userGroupIds.includes(g.id)}
                        checked={userAdminGroupIds.includes(g.id)}
                        onChange={(e)=>{
                          if (e.target.checked) setUserAdminGroupIds(prev => [...prev, g.id]);
                          else setUserAdminGroupIds(prev => prev.filter(id => id !== g.id));
                        }}
                      />
                      Diretor
                    </label>
                  )}
                </div>
              ))}
              {(!allGroups || allGroups.length===0) && (
                <div>Nenhum setor disponível.</div>
              )}
            </div>
            <div className="manageusers-modal-actions">
              <button className="btn" onClick={() => setShowGroupsModal(false)}>Cancelar</button>
              <button
                className="btn primary"
                onClick={async ()=>{
                  await setUserGroups(
                    selectedUser.id,
                    userGroupIds,
                    isAdmin ? userAdminGroupIds : null
                  );
                  setShowGroupsModal(false);
                  fetchUsers();
                }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reset de Senha */}
      {showResetPasswordModal && selectedUser && (
        <ResetPasswordModal
          user={selectedUser}
          onClose={() => {
            setShowResetPasswordModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setSuccess('Senha resetada com sucesso');
            setTimeout(() => setSuccess(''), 3000);
          }}
          onError={setError}
        />
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && selectedUser && (
        <div className="manageusers-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="manageusers-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmar Exclusão</h3>
            <p>
              Tem certeza que deseja deletar o usuário <strong>{selectedUser.username}</strong>?
              Esta ação não pode ser desfeita.
            </p>
            <div className="manageusers-modal-actions">
              <button
                className="manageusers-cancel-button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
              >
                Cancelar
              </button>
              <button className="manageusers-delete-button" onClick={deleteUser}>
                <Trash2 size={16} />
                Deletar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Modal de Edição de Usuário
const EditUserModal = ({ user, onClose, onSuccess, onError, allowAdminToggle = true }) => {
  const [formData, setFormData] = useState({
    email: user.email,
    full_name: user.full_name || '',
    is_admin: user.is_admin
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData };
      if (!allowAdminToggle) {
        delete payload.is_admin;
      }
      await api.put(`/users/${user.id}`, payload);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Erro ao atualizar usuário:', err);
      onError(err.response?.data?.detail || 'Erro ao atualizar usuário');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manageusers-modal-overlay" onClick={onClose}>
      <div className="manageusers-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Editar Usuário</h3>
        <form onSubmit={handleSubmit} className="manageusers-modal-form">
          <div className="manageusers-form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          <div className="manageusers-form-group">
            <label>Nome Completo</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              disabled={loading}
            />
          </div>

          {allowAdminToggle && (
          <div className="manageusers-form-group">
            <div className="manageusers-checkbox-group">
              <input
                type="checkbox"
                id="edit_is_admin"
                checked={formData.is_admin}
                onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                disabled={loading}
              />
              <label htmlFor="edit_is_admin" className="manageusers-checkbox-label">
                Usuário Administrador Geral
              </label>
            </div>
          </div>
          )}

          <div className="manageusers-modal-actions">
            <button
              type="button"
              className="manageusers-cancel-button"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Salvar
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal de Reset de Senha
const ResetPasswordModal = ({ user, onClose, onSuccess, onError }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      onError('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 6) {
      onError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await api.post(`/users/${user.id}/reset-password`, {
        user_id: user.id,
        new_password: newPassword
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Erro ao resetar senha:', err);
      onError(err.response?.data?.detail || 'Erro ao resetar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manageusers-modal-overlay" onClick={onClose}>
      <div className="manageusers-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Resetar Senha</h3>
        <p className="manageusers-modal-description">
          Resetar senha do usuário <strong>{user.username}</strong>
        </p>
        <form onSubmit={handleSubmit} className="manageusers-modal-form">
          <div className="manageusers-form-group">
            <label>Nova Senha</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              required
              disabled={loading}
            />
          </div>

          <div className="manageusers-form-group">
            <label>Confirmar Nova Senha</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirme a senha"
              required
              disabled={loading}
            />
          </div>

          <div className="manageusers-modal-actions">
            <button
              type="button"
              className="manageusers-cancel-button"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  Resetando...
                </>
              ) : (
                <>
                  <Lock size={16} />
                  Resetar Senha
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManageUsers;

